import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";
import moment from "moment";

import {
  MONTH_NAMES,
  ORDINAL_NUMBERS,
  WEEK_DAYS_NAMES,
} from "@/Constants/common";
import { _fetchFieldData } from "@/services/clientCommon";
import {
  _deleteJobTemplate,
  _fetchDayWiseRates,
  _fetchBankShortListCandidate,
  _fetchJobTemplates,
} from "@/services/createjob";
import { createJobValidations } from "@/utilities/CommonText";
import { defaultRepeatObj } from "@/utilities/defaultData";
import {
  handleAPICatch,
  isDateBetween,
  showErrorToast,
  showSuccessToast,
} from "@/utilities/utils";

import { formatInvitedCandidates } from "../utilities/dataFormatter";

const initialState = {
  activeStep: 1,
  isLoading: false,
  templateIsLoading: false,
  contractTemplates: [],
  candidateRoleList: [],
  skillsList: [],
  selectedShortlistCandidate: [],
  selectedCandidateIds: [],
  activeTemplate: null,
  activeTemplateType: "custom",
  stepsValidated: {
    jobDetails: true,
    selectDates: false,
    timeSelection: false,
    verification: false,
  },
  timeSelectionsPresets: [
    {
      label: "15 min",
      value: "00:15",
    },
    {
      label: "30 min",
      value: "00:30",
    },
    {
      label: "45 min",
      value: "00:45",
    },
    {
      label: "1 hour",
      value: "01:00",
    },
  ],
  breakTimePreset: [
    {
      label: "15 min",
      value: "00:15",
    },
    {
      label: "30 min",
      value: "00:30",
    },
    {
      label: "45 min",
      value: "00:45",
    },
    {
      label: "1 hour",
      value: "01:00",
    },
  ], // not in use
  adminTimePreset: [
    {
      label: "15 min",
      value: "00:15",
    },
    {
      label: "30 min",
      value: "00:30",
    },
    {
      label: "45 min",
      value: "00:45",
    },
    {
      label: "1 hour",
      value: "01:00",
    },
  ], // not in use
  jobData: {
    jobTitle: "",
    jobDescription: "",
    roleId: null,
    parentID: null,
    chargeRateType: "",
    skills: [],
    selectDatesRanges: [],
    selectDateJobDates: [],
    startDate: null,
    endDate: null,
    rateCardList: [],
    startTime: null,
    endTime: null,
    weekDays: [],
    breakTime: "00:00",
    isBreakTimeCustom: false,
    noOfVisit: null,
    clientChargesPerVisit: 0,
    totalClientChargesPerVisit: 0,
    adminTime: "00:00",
    isAdminTimeCustom: false,
    admin_per: null,
    visit_per: null,
    pricePerVisit: null,
    client_rate_id: null,
    vatStructure: null,
    rateObj: null,
    totalDays: 0, // total days between start and end date
    client_per: null,
    rateName: null,
    dayRate: null,
    totalHours: 0,
    totalAdminCharge: 0,
    totalCost: 0,
    totalCandidatePay: 0,
    applocumServiceFee: 0,
    setAsTemplate: false,
    is_for_appLocum_candidates: false,
    is_book_candidate: false,
    book_candidate_id: [],
  },
  invitedBankCandidateForContractModal: false,
  isShortListLoading: false,
  filters: {
    search: "",
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  totalCount: 0,
  shortlistCandidateData: [],
  isSelectedAll: false,
  jobShiftObj: [],
  selectedJobShiftObj: [],
  jobShiftObjFilters: {
    dates: [],
  },
  getDayWiseApiLoading: 0,
  enableMultipleShiftEditing: false,
  firstMultipleSelectedJobShiftObj: {},
  toggleSelected: false,
  isUnitsValid: true,
  isStartEndDateDisabled: false,

  // recurring job
  recurringListingPresets: {
    repeatPresets: [
      {
        label: "Every Day",
        value: "everyDay",
      },
      {
        label: "Every Week",
        value: "everyWeek",
      },
      {
        label: "Every Month",
        value: "everyMonth",
      },
      {
        label: "Every Year",
        value: "everyYear",
      },
      {
        label: "Custom",
        value: "custom",
      },
    ],
    frequencyPresets: [
      {
        label: "Daily",
        value: "daily",
      },
      {
        label: "Weekly",
        value: "weekly",
      },
      {
        label: "Monthly",
        value: "monthly",
      },
      {
        label: "Yearly",
        value: "yearly",
      },
    ],
    weekDaysNamesPresets: WEEK_DAYS_NAMES,
    monthsNamesPresets: MONTH_NAMES,
    daysOfWeekPresets: ORDINAL_NUMBERS,
  },
  repeatObj: defaultRepeatObj,
  customRecurrenceModalOpen: false,
  // recurring job curring job
};
/**
 * Calculate the client's payment based on the client percentage and the rate.
 *
 * @param {number} client_per - the client's percentage
 * @param {number} rate - the rate
 * @return {number} the calculated client payment
 */
const getClientPay = (client_per, rate, matchingChargeType) => {
  return matchingChargeType === "amount"
    ? rate + client_per
    : (client_per / 100) * rate + rate;
};

const getClientVisitPayFee = (
  rate = 0,
  priceOfVisitPayCharges = 0,
  noOfVisit = 0,
  totalDays = 0,
) => {
  rate = Number(rate) || 0;
  priceOfVisitPayCharges = Number(priceOfVisitPayCharges) || 0;
  noOfVisit = Number(noOfVisit) || 0;
  totalDays = Number(totalDays) || 0;

  return (priceOfVisitPayCharges / 100) * rate * noOfVisit * totalDays || 0;
};

/**
 * Calculate the unit after deducting the break time.
 *
 * @param {number} unit - the initial unit value
 * @param {string} breakTime - the break time in "HH:mm" format
 * @return {number} the unit value after deducting the break time
 */
const calculateUnit = (unit, breakTime) => {
  let breakUnit = 0;
  if (breakTime !== null && breakTime !== undefined && breakTime !== "") {
    let breakDuration = moment.duration(breakTime, "HH:mm");
    let breakHours = parseInt(breakDuration.asHours());
    let breakMinutes = parseInt(breakDuration.asMinutes()) - breakHours * 60;
    breakUnit = breakHours + breakMinutes / 60;
  }
  return unit - breakUnit;
};

const getAdminCharge = (adminTime, admin_per, client_rate) => {
  // let adminUnit = 0;
  // if (adminTime !== null && adminTime !== undefined && adminTime !== "") {
  //   let adminDuration = moment.duration(adminTime, "HH:mm");
  //   let adminHours = parseInt(adminDuration.asHours());
  //   let adminMinutes = parseInt(adminDuration.asMinutes()) - adminHours * 60;
  //   adminUnit = adminHours + adminMinutes;
  // }

  // convert admin time in minute
  let adminUnit = moment.duration(adminTime, "HH:mm").asMinutes(); // Represents the duration in minutes calculated from the adminTime value.
  let adminCharge = (client_rate / 60) * adminUnit; // Represents the admin charge calculated from the client rate and admin unit.
  return parseFloat(
    (adminCharge + (adminCharge * parseFloat(admin_per)) / 100).toFixed(2),
  );
  // return adminUnit * parseFloat(admin_per);
};

export const fetchJobTemplates = createAsyncThunk(
  "clientCreateJob/fetchJobTemplates",
  async (data, { dispatch }) => {
    const response = _fetchJobTemplates(data);
    response
      .then((res) => {
        const {
          data: { template_list },
        } = res;
        dispatch(updateContractTemplatesList(template_list));
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        dispatch(updateTemplateLoading(false));
      });
    return response;
  },
);

// _fetchFieldData
export const fetchSkills = createAsyncThunk(
  "clientCreateJob/fetchSkills",
  async (data, { dispatch }) => {
    const response = _fetchFieldData(data);
    response
      .then((res) => {
        const {
          data: { field_data },
        } = res;
        dispatch(updateCandidateRoleList(field_data));
      })
      .catch((error) => {
        handleAPICatch(error);
      });
    return response;
  },
);

export const fetchRoles = createAsyncThunk(
  "clientCreateJob/fetchRoles",
  async (data, { dispatch }) => {
    let fieldPostData = {
      field_type: "role",
    };
    const response = _fetchFieldData(fieldPostData);
    response
      .then((res) => {
        const {
          data: { client_type, field_data },
        } = res;
        if (client_type) {
          let gradePostData = {
            field_type: "grade",
            id: client_type,
            calling_from: "create_job",
          };
          dispatch(fetchSkills(gradePostData));
        }
      })
      .catch((error) => {
        handleAPICatch(error);
      });
    return response;
  },
);

export const deleteJobTemplate = createAsyncThunk(
  "clientCreateJob/deleteJobTemplate",
  async (data, thunkAPI) => {
    const id = data.template_id;
    const response = _deleteJobTemplate(data);
    response
      .then((res) => {
        const {
          data: { data, message },
        } = res;
        showSuccessToast(message);
        let curTemplates =
          thunkAPI.getState().clientCreateJob.contractTemplates;
        let allTemplates = curTemplates.filter(
          (template) => template.id !== id,
        );
        thunkAPI.dispatch(updateContractTemplatesList(allTemplates));
      })
      .catch((error) => {
        // handleAPICatch(error);
      });
  },
);

export const getDayWiseRates = createAsyncThunk(
  "clientCreateJob/getDayWiseRates",
  async (data, thunkAPI) => {
    let fromEditing = _.has(data, "fromEditing") ? data.fromEditing : false;
    let sendingData = data;
    let jobData = thunkAPI.getState().clientCreateJob.jobData;

    // job details - i check condition if more rates found so based on time selection page - rate structure- get id of selection and check select id and listing id and match id and store & share them to api
    const matchingRateObj =
      jobData?.rateObj?.find(
        (rate) => rate.data.id === jobData?.rateCardList,
      ) || [];

    const rate_obj = matchingRateObj
      ? JSON.stringify(matchingRateObj?.data)
      : JSON.stringify(jobData.rateObj[0].data);

    // Check charge type is amount or percentage - if amount then use the amount else use the percentage - hourly basic amount and percentage count..
    // 1. AMount calculation -> candidate pay - 120 and hour 3 = without fee -> 360
    //                        If  Amount = 35 => so appLocum Fee's 35 * 3 = 105 => total = 465

    // 2. percentage calculation -> candidate pay - 120 and hour 3 = without fee -> 360
    //                        If percentage = 13% => so appLocum Fee's (360 * 13%) = 46.8 => total = 406.8

    const matchingChargeType =
      jobData?.rateObj?.find((rate) => rate.data.id === jobData?.rateCardList)
        ?.charge_rate_type || "percentage";

    let dayRate = matchingRateObj
      ? parseFloat(matchingRateObj?.is_day_rate)
      : jobData.dayRate;

    const _data = {
      rate_obj: rate_obj, // rateObj came from api call of role
      start_time: fromEditing ? data._startTime : jobData.startTime, // if from editing then use the new start time else use the old one
      end_time: fromEditing ? data._endTime : jobData.endTime, // if from editing then use the new end time else use the old one
      day: data.day, // day name
      is_day_rate: dayRate,
      start_date: jobData.startDate, // start date [from datepicker]
      end_date: jobData.endDate, // end date [from datepicker]
      selected_date: data.selected_date, // if from editing then use the new current date else use the old one
    };
    // return;
    const response = _fetchDayWiseRates(_data);
    // const response = checkTimeBasedRates(_data);
    thunkAPI.dispatch(updateGetDayWiseApiCallLoading("+")); // incrementing the api call count
    let client_per = matchingRateObj
      ? parseFloat(matchingRateObj?.charge_percentage_on_pay_rate)
      : parseFloat(jobData.client_per);
    let clientVATStructure = matchingRateObj
      ? matchingRateObj?.data?.vat_structure
      : jobData.vatStructure;

    thunkAPI.dispatch(
      updateJobData({
        client_per: client_per,
        client_rate_id: jobData.rateCardList,
        vatStructure: clientVATStructure,
        dayRate: dayRate,
        chargeRateType: matchingChargeType,
        totalClientChargesPerVisit: 0,
      }),
    );
    response
      .then((res) => {
        const {
          data: { rateArr },
        } = res;
        for (let i = 0; i < rateArr.length; i++) {
          let newData = rateArr[i];
          const {
            date,
            is_bank_holiday,
            rate: { rate: rateARR, rate_found },
          } = newData;

          // if(state.jobData.dayRate){

          // }

          if (is_bank_holiday) {
            // showErrorToast(`${date} is a bank holiday`, {
            //   id: "bank-holiday",
            // });
            thunkAPI.dispatch(
              removeDayFromJobShiftObj({
                removeBy: "date",
                value: date,
              }),
            );
          } else if (!rate_found) {
            showErrorToast(createJobValidations.noRatesFound, {
              id: "no-rates-found",
            });
            let _singleShiftObj = {
              // day: sendingData.day, // day name
              break: fromEditing ? sendingData._breakTime : jobData.breakTime, // if from editing then use the new break time else use from the jobData
              startTime: fromEditing
                ? sendingData._startTime
                : jobData.startTime, // if from editing then use the new start time else use the old one
              endTime: fromEditing ? sendingData._endTime : jobData.endTime, // if from editing then use the new end time else use the old one
              total: 0,
              totalUnit: 0,
              rate_values: [],
              admin: fromEditing ? sendingData._adminTime : jobData.adminTime,
              currentDate: date,
              isRateFound: false,
              isUnitInvalid: false,
              adminCharge: 0,
            };
            thunkAPI.dispatch(updateSingleJobShift(_singleShiftObj));
          } else {
            let rate_values = [];
            let total = 0;
            let totalUnit = 0;
            let _unitArr = _.map(rateARR, "unit");
            let maxUnit = parseFloat(_.max(_unitArr));
            let breakTimeMinused = false;
            rateARR.forEach((rateObj) => {
              let rateObjUnit = parseFloat(rateObj.unit.toFixed(2));
              if (rateObj.unit == maxUnit && !breakTimeMinused) {
                rateObjUnit = parseFloat(
                  calculateUnit(
                    maxUnit,
                    fromEditing ? sendingData._breakTime : jobData.breakTime,
                  ).toFixed(2),
                );
                breakTimeMinused = true;
              }
              let newTotal = parseFloat(
                (rateObj.candidate_pay * rateObjUnit).toFixed(2),
              );
              totalUnit += parseFloat(rateObjUnit);
              total += newTotal;
              let obj = {
                actualUnit: rateObj.unit, // unit came from api side
                rate: rateObj.candidate_pay, // rate
                clientPay: getClientPay(
                  client_per,
                  rateObj?.candidate_pay,
                  matchingChargeType,
                ),
                rateName: rateObj.rate_name, // ratename ["Weekday","Evening"]
                unit: parseFloat(rateObjUnit),
                total: newTotal,
                shiftAdminCharge: getAdminCharge(
                  fromEditing ? sendingData._adminTime : jobData.adminTime,
                  jobData.admin_per,
                  getClientPay(
                    client_per,
                    rateObj?.candidate_pay,
                    matchingChargeType,
                  ),
                ),
              };

              // thunkAPI.dispatch(
              //   updateJobData({
              //     totalClientChargesPerVisit: getClientVisitPayFee(rate_values?.rate, thunkAPI.getState()?.clientCreateJob.jobData?.priceOfVisitPayCharges, thunkAPI.getState()?.clientCreateJob.jobData?.noOfVisit, thunkAPI.getState()?.clientCreateJob.jobData?.totalDays),
              //   }),
              // );

              rate_values.push(obj);
            });

            //  Perform the calculation after the array is fully populated of rate_values - because want multiple rate values of the same day
            // const totalClientChargesPerVisit = rate_values.reduce((sum, item) => {
            //   const priceOfVisitPayCharges = thunkAPI.getState()?.clientCreateJob.jobData?.priceOfVisitPayCharges;
            //   const noOfVisit = thunkAPI.getState()?.clientCreateJob.jobData?.noOfVisit;
            //   const totalDays = thunkAPI.getState()?.clientCreateJob.jobData?.totalDays;

            //   console.log("Processing item:", item);
            //   console.log("Values Passed ------------: ", {
            //     rate: item.rate,
            //     priceOfVisitPayCharges,
            //     noOfVisit,
            //     totalDays
            //   });

            //   const calculatedFee = getClientVisitPayFee(item.rate, priceOfVisitPayCharges, noOfVisit, totalDays);
            //   console.log("Calculated Fee:", calculatedFee);

            //   return sum + (calculatedFee || 0); // Ensure NaN doesn't break the sum
            // }, 0);

            // check total candidate pay and find average rate and then calculate the visit fee
            const clientChargesPerVisit = (() => {
              const { jobData } = thunkAPI.getState()?.clientCreateJob || {};
              const { priceOfVisitPayCharges, noOfVisit, totalDays } =
                jobData || {};

              // Calculate the average rate
              const totalRates = rate_values.reduce(
                (sum, item) => sum + item.rate,
                0,
              );
              const averageRate =
                rate_values.length > 0 ? totalRates / rate_values.length : 0; // Avoid division by zero

              // Call getClientVisitPayFee with the average rate
              const calculatedFee = getClientVisitPayFee(
                averageRate,
                priceOfVisitPayCharges,
                noOfVisit,
                1,
              );
              return calculatedFee;
            })();

            // Update the state with the calculated totalClientChargesPerVisit
            // thunkAPI.dispatch(updateJobData({ totalClientChargesPerVisit }));

            let adminCharge = _.reduce(
              rate_values,
              (sum, n) => sum + n.shiftAdminCharge,
              0,
            );

            let _singleShiftObj = {
              // day: day, // day name
              break: fromEditing ? sendingData._breakTime : jobData.breakTime, // if from editing then use the new break time else use from the jobData
              startTime: fromEditing
                ? sendingData._startTime
                : jobData.startTime, // if from editing then use the new start time else use the old one
              endTime: fromEditing ? sendingData._endTime : jobData.endTime, // if from editing then use the new end time else use the old one
              admin: fromEditing ? sendingData._adminTime : jobData.adminTime,
              adminCharge,
              total,
              totalUnit,
              rate_values,
              currentDate: date,
              isRateFound: true,
              isUnitInvalid: totalUnit < 0.5,
              clientChargesPerVisit,
            };
            thunkAPI.dispatch(updateSingleJobShift(_singleShiftObj));
          }
        }
      })
      .catch((error) => {
        // console.log("error", error);
        const {
          response: {
            data: { data, message },
            statusText,
          },
        } = error;
        let date = data.date ? data.date : null;

        if (message) {
          // removing if bank holiday is in the message
          let trimmedMessage = message.replace(/\s/g, "");
          if (date && trimmedMessage.toLowerCase().includes("bankholiday")) {
            showErrorToast(message, {
              id: "bank-holiday",
            });
            thunkAPI.dispatch(
              removeDayFromJobShiftObj({
                removeBy: "date",
                value: date,
              }),
            );
          } else {
            showErrorToast(message, {
              id: "no-rates-found",
            });
            let _singleShiftObj = {
              // day: sendingData.day, // day name
              break: fromEditing ? sendingData._breakTime : jobData.breakTime, // if from editing then use the new break time else use from the jobData
              startTime: fromEditing
                ? sendingData._startTime
                : jobData.startTime, // if from editing then use the new start time else use the old one
              endTime: fromEditing ? sendingData._endTime : jobData.endTime, // if from editing then use the new end time else use the old one
              total: 0,
              totalUnit: 0,
              rate_values: [],
              admin: fromEditing ? sendingData._adminTime : jobData.adminTime,
              adminCharge: 0,
              currentDate: sendingData.selected_date,
              isRateFound: false,
              isUnitInvalid: true,
            };
            thunkAPI.dispatch(updateSingleJobShift(_singleShiftObj));
          }
          // removing if bank holiday is in the message
        } else {
          showErrorToast(statusText, {
            id: "getDayWiseRates-failed",
          });
        }
      })
      .finally(() => {
        thunkAPI.dispatch(updateGetDayWiseApiCallLoading("-"));
      });
  },
);

export const fetchBankShortlistCandidates = createAsyncThunk(
  "clientCreateJob/fetchBankShortlistCandidates",
  async (data, { dispatch, getState }) => {
    const clientCreateJob = getState().clientCreateJob;
    let paginationData = clientCreateJob.pagination;
    let filtersData = clientCreateJob.filters;
    let sortingData = clientCreateJob.sorting;

    if (_.isEmpty(data)) {
      data = {
        ...filtersData,
      };
    }
    if (paginationData.isLastPage) {
      return dispatch(setBankShortListCandidatesLoading(false));
    }

    const _data = {
      ...data,
      contract_role_id: clientCreateJob.jobData.roleId,
      currentPage: paginationData.currentPage,
      ...sortingData,
    };

    dispatch(toggleSelectedAll(false));

    const response = _fetchBankShortListCandidate(_data);
    response
      .then((res) => {
        const {
          data: { candidates, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        let data = formatInvitedCandidates(candidates);
        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );
        let prevList =
          currentPage === 1
            ? []
            : _.uniq(getState().clientCreateJob.shortlistCandidateData);
        let firstcandidate_email =
          data.length > 0 ? data[0].candidate_email : null;
        dispatch(updateTotalCount(totalCount));
        if (
          prevList.length > 0 &&
          prevList.find(
            (item) => item.candidate_email === firstcandidate_email,
          ) !== undefined
        ) {
          //
        } else {
          dispatch(updateBankShortListCandidatesList([...prevList, ...data]));
        }
      })
      .catch((error) => {
        // console.log("error", error);
        handleAPICatch(error);
        // return rejectWithValue(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setBankShortListCandidatesLoading(false));
        }, 10);
      });
  },
);

const clientCreateJobSlice = createSlice({
  name: "clientCreateJob",
  initialState,
  reducers: {
    /**
     * Update the active step in the state.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing the payload
     * @return {void}
     */
    updateActiveStep: (state, action) => {
      state.activeStep = action.payload;
    },
    /**
     * Updates the loading state with the value from the action payload.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object containing the payload
     */
    updateLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    updateTemplateLoading: (state, action) => {
      state.templateIsLoading = action.payload;
    },
    /**
     * Updates the steps validated in the state with the provided action payload.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing payload
     */
    updateStepsValidated: (state, action) => {
      state.stepsValidated = {
        ...state.stepsValidated,
        ...action.payload,
      };
    },
    /**
     * Update the toggleSelected property in the state by toggling its value.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object
     * @return {void}
     */
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    /**
     * Updates the contract templates list in the state with the provided payload.
     *
     * @param {type} state - description of state parameter
     * @param {type} action - description of action parameter
     * @return {type} description of return value
     */
    updateContractTemplatesList: (state, action) => {
      state.contractTemplates = action.payload; // updated contract templates list
    },
    /**
     * Updates the active template in the state based on the given action.
     *
     * @param {object} state - the current state object
     * @param {object} action - the action object containing the payload
     */
    updateActiveTemplate: (state, action) => {
      state.activeTemplate = action.payload.template;
      if (action.payload.template) {
        let templateObj = action.payload.template;
        const {
          template_title,
          template_description,
          template_role_id,
          template_skill_ids,
          template_role_parent_id,
          name,
        } = templateObj;
        if (templateObj) {
          state.jobData = {
            ...state.jobData,
            jobTitle: template_title,
            jobDescription: template_description,
            roleId: parseInt(template_role_id),
            parentID: template_role_parent_id,
            roleName: name,
            skills: template_skill_ids
              ? template_skill_ids.split(",").map((item) => parseInt(item))
              : [],
          };
        }
      }
    },
    updateActiveTemplateType: (state, action) => {
      state.activeTemplateType = action.payload;
    },
    /**
     * Updates the job shift list in the state with the provided payload.
     *
     * @param {type} state - the state object
     * @param {type} action - the action object containing the payload
     */
    updateJobShiftList: (state, action) => {
      state.jobShiftObj = action.payload; // updates whole list
    },
    /**
     * Updates a single job shift in the state based on the given action.
     *
     * @param {Object} state - the current state object
     * @param {Object} action - the action object containing payload
     */
    updateSingleJobShift: (state, action) => {
      // let day = action.payload.day;
      let currentDate = action.payload.currentDate || null;
      // let hasUnitLessThanHalf = false;
      state.jobShiftObj = state.jobShiftObj.map((ele) => {
        if (ele.date === currentDate) {
          return {
            ...ele,
            ...action.payload,
          };
        } else {
          return ele;
        }
      });

      // calculating total values
      // let totalHours = _.map(state.jobShiftObj, "totalUnit")
      // .map(num => Number(num) || 0) // Convert to number, default to 0 if invalid
      // .reduce((a, b) => a + b, 0);

      let totalHours = Array.isArray(state.jobShiftObj)
        ? _.map(state.jobShiftObj, "totalUnit")
            .map((num) => Number(num) || 0)
            .reduce((a, b) => a + b, 0)
        : 0;

      // sum of totalHours of all days
      let totalCandidatePay = parseFloat(
        _.map(state.jobShiftObj, "total")
          .reduce((a, b) => a + b, 0)
          .toFixed(2),
      ); // sum of total of all days

      let totalAdminCharge = parseFloat(
        _.map(state.jobShiftObj, "adminCharge")
          .reduce((a, b) => a + b, 0)
          .toFixed(2),
      ); // sum of total of adminCharge

      // totalClientChargesPerVisit
      let totalClientChargesPerVisit = parseFloat(
        _.map(state.jobShiftObj, "clientChargesPerVisit")
          .reduce((a, b) => a + b, 0)
          .toFixed(2),
      );

      let findExtraChargeInVisitPay =
        state.jobData?.chargeRateType == "amount"
          ? state.jobData?.noOfVisit > 0 &&
            totalCandidatePay != 0 &&
            state.jobData?.priceOfVisitPayCharges != 0
            ? (100 / state.jobData?.priceOfVisitPayCharges) *
              state.jobData?.noOfVisit *
              state.jobData?.totalDays
            : 0
          : state.jobData?.noOfVisit > 0 && totalCandidatePay != 0
            ? (totalClientChargesPerVisit * state.jobData.client_per) / 100
            : 0;

      const clientCharges =
        state.jobData?.chargeRateType === "amount"
          ? (Number(totalHours) || 0) *
              (Number(state.jobData?.client_per) || 0) +
            (Number(findExtraChargeInVisitPay) || 0)
          : ((Number(totalCandidatePay) || 0) *
              (Number(state.jobData?.client_per) || 0)) /
              100 +
            (Number(findExtraChargeInVisitPay) || 0);

      state.jobData = {
        ...state.jobData,
        totalDays: state.jobShiftObj.length,
        totalClientChargesPerVisit: totalClientChargesPerVisit || 0,
        totalHours: parseFloat(totalHours.toFixed(2)),
        totalCandidatePay,
        applocumServiceFee: clientCharges,
        totalCost: parseFloat(
          (
            totalCandidatePay +
            totalClientChargesPerVisit +
            clientCharges
          ).toFixed(2),
        ),
        totalAdminCharge,
      };
      // state.isUnitsValid = hasUnitLessThanHalf;
    },
    /**
     * Updates the job shift object from local state and action payload.
     *
     * @param {Object} state - the current state
     * @param {Object} action - the action object
     * @return {void}
     */
    updateJobShiftObjFromLocal: (state, action) => {
      let fromTimeSelection = action?.payload?.fromTimeSelection || false; // if from time selection then take the values from jobData else take from payload
      let currentDate = action?.payload?.currentDate || null; // if current date is available then take the values from payload
      // let hasUnitLessThanHalf = false;
      state.jobShiftObj = state.jobShiftObj.map((ele) => {
        let totalUnit = 0;
        let breakTime = fromTimeSelection ? state.jobData.breakTime : ele.break; // taking from jobData if called from time selection
        let adminTime = fromTimeSelection ? state.jobData.adminTime : ele.admin; // taking from jobData if called from time selection
        let startTime = fromTimeSelection
          ? state.jobData.startTime
          : ele.startTime; // taking from jobData
        let endTime = fromTimeSelection ? state.jobData.endTime : ele.endTime; // taking from jobData if called from time selection
        if (currentDate === ele.date) {
          // if date is current date then take the values from payload
          breakTime = action?.payload?.break;
          adminTime = action?.payload?.admin;
          startTime = action?.payload?.startTime;
          endTime = action?.payload?.endTime;
        }
        let newMainTotal = 0;
        let actualUnitArray = _.map(ele.rate_values, "actualUnit"); // array of actualUnit
        let actualMaxUnit = _.max(actualUnitArray); // max of actualUnit
        let breakTimeMinused = false;
        let rate_values = ele.rate_values.map((rateVal) => {
          let newUnit = parseFloat(rateVal.actualUnit.toFixed(2));
          if (rateVal.actualUnit == actualMaxUnit && !breakTimeMinused) {
            newUnit = parseFloat(
              calculateUnit(actualMaxUnit, breakTime).toFixed(2),
            );
            breakTimeMinused = true;
          }
          let newTotal = parseFloat((rateVal.rate * newUnit).toFixed(2));
          newMainTotal += parseFloat(newTotal.toFixed(2));
          totalUnit += parseFloat(newUnit);
          // if (newUnit < 0.5) {
          //   hasUnitLessThanHalf = true;
          // }
          let shiftAdminCharge = getAdminCharge(
            adminTime,
            state.jobData.admin_per,
            rateVal.clientPay,
          );
          return {
            ...rateVal,
            unit: parseFloat(newUnit),
            total: newTotal,
            shiftAdminCharge,
          };
        });
        let adminCharge = _.reduce(
          rate_values,
          (sum, n) => sum + n.shiftAdminCharge,
          0,
        );
        return {
          ...ele,
          break: breakTime,
          startTime: startTime,
          admin: adminTime,
          adminCharge,
          endTime: endTime,
          total: newMainTotal,
          totalUnit,
          rate_values,
          isUnitInvalid: rate_values.length ? totalUnit < 0.5 : false,
        };
      });

      // let totalHours = _.map(state.jobShiftObj, "totalUnit").reduce(
      //   (a, b) => a + b,
      //   0,
      // ); // sum of totalUnit of all days
      let totalHours = Array.isArray(state.jobShiftObj)
        ? _.map(state.jobShiftObj, "totalUnit")
            .map((num) => Number(num) || 0)
            .reduce((a, b) => a + b, 0)
        : 0;

      let totalCandidatePay = parseFloat(
        _.map(state.jobShiftObj, "total")
          .reduce((a, b) => a + b, 0)
          .toFixed(2),
      ); // sum of total of all days

      let totalAdminCharge = parseFloat(
        _.map(state.jobShiftObj, "adminCharge")
          .reduce((a, b) => a + b, 0)
          .toFixed(2),
      ); // sum of total of adminCharge

      let findExtraChargeInVisitPay =
        state.jobData?.chargeRateType == "amount"
          ? state.jobData?.noOfVisit > 0 &&
            totalCandidatePay != 0 &&
            state.jobData?.priceOfVisitPayCharges != 0
            ? (100 / state.jobData?.priceOfVisitPayCharges) *
              state.jobData?.noOfVisit *
              state.jobData?.totalDays
            : 0
          : state.jobData?.noOfVisit > 0 && totalCandidatePay != 0
            ? (state.jobData?.totalClientChargesPerVisit *
                state.jobData.client_per) /
              100
            : 0;

      const clientCharges =
        state.jobData?.chargeRateType === "amount"
          ? (Number(totalHours) || 0) *
              (Number(state.jobData?.client_per) || 0) +
            (Number(findExtraChargeInVisitPay) || 0)
          : ((Number(totalCandidatePay) || 0) *
              (Number(state.jobData?.client_per) || 0)) /
              100 +
            (Number(findExtraChargeInVisitPay) || 0);
      state.jobData = {
        ...state.jobData,
        totalDays: state.jobShiftObj.length,
        totalHours: parseFloat(totalHours.toFixed(2)),
        totalCandidatePay,
        applocumServiceFee: clientCharges,
        totalCost: parseFloat(
          (
            totalCandidatePay +
            state.jobData.totalClientChargesPerVisit +
            clientCharges
          ).toFixed(2),
        ),
        totalAdminCharge,
      };
      // state.isUnitsValid = hasUnitLessThanHalf;
    },
    /**
     * A function to remove a specific day from the job shift object.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing the payload
     * @return {void} This function does not return anything
     */
    removeDayFromJobShiftObj: (state, action) => {
      // removing days if not available a rate for it
      let removeBy = action.payload.removeBy;
      let value = action.payload.value;
      let jobShiftObj = state.jobShiftObj;
      let newJobShiftObj = jobShiftObj
        .map((ele) => {
          if (ele[removeBy] !== value) {
            return ele;
          }
          return null;
        })
        .filter((ele) => ele !== null);
      state.jobShiftObj = newJobShiftObj;

      // calculating total values
      // let totalHours = _.map(state.jobShiftObj, "totalUnit").reduce(
      //   (a, b) => a + b,
      //   0,
      // ); // sum of totalUnit of all days

      let totalHours = Array.isArray(state.jobShiftObj)
        ? _.map(state.jobShiftObj, "totalUnit")
            .map((num) => Number(num) || 0)
            .reduce((a, b) => a + b, 0)
        : 0;

      let totalCandidatePay = parseFloat(
        _.map(state.jobShiftObj, "total")
          .reduce((a, b) => a + b, 0)
          .toFixed(2),
      ); // sum of total of all days

      let totalAdminCharge = parseFloat(
        _.map(state.jobShiftObj, "adminCharge")
          .reduce((a, b) => a + b, 0)
          .toFixed(2),
      ); // sum of total of adminCharge

      let findExtraChargeInVisitPay =
        state.jobData?.chargeRateType == "amount"
          ? state.jobData?.noOfVisit > 0 &&
            totalCandidatePay != 0 &&
            state.jobData?.priceOfVisitPayCharges != 0
            ? (100 / state.jobData?.priceOfVisitPayCharges) *
              state.jobData?.noOfVisit *
              state.jobData?.totalDays
            : 0
          : state.jobData?.noOfVisit > 0 && totalCandidatePay != 0
            ? (state.jobData?.totalClientChargesPerVisit *
                state.jobData.client_per) /
              100
            : 0;

      const clientCharges =
        state.jobData?.chargeRateType === "amount"
          ? (Number(totalHours) || 0) *
              (Number(state.jobData?.client_per) || 0) +
            (Number(findExtraChargeInVisitPay) || 0)
          : ((Number(totalCandidatePay) || 0) *
              (Number(state.jobData?.client_per) || 0)) /
              100 +
            (Number(findExtraChargeInVisitPay) || 0);

      state.jobData = {
        ...state.jobData,
        totalDays: state.jobShiftObj.length,
        totalHours: parseFloat(totalHours.toFixed(2)),
        totalCandidatePay,
        applocumServiceFee: clientCharges,
        totalCost: parseFloat(
          (
            totalCandidatePay +
            state.jobData.totalClientChargesPerVisit +
            clientCharges
          ).toFixed(2),
        ),
        totalAdminCharge,
      };
    },
    /**
     * Updates the candidate role list in the state with the provided payload.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing the payload
     * @return {void}
     */
    updateCandidateRoleList: (state, action) => {
      state.candidateRoleList = action.payload;
    },
    /**
     * Update the skills list in the state with the provided payload.
     *
     * @param {type} state - the current state object
     * @param {type} action - the action object containing the payload
     * @return {type}
     */
    updateSkillsList: (state, action) => {
      state.skillsList = action.payload;
    },
    /**
     * Updates the jobData in the state with the payload from the action.
     *
     * @param {Object} state - the current state object
     * @param {Object} action - the action object containing the payload
     */
    updateJobData: (state, action) => {
      let callback = action.payload.callback ? action.payload.callback : null;
      if (action.payload.callback) {
        delete action.payload.callback;
      }
      state.jobData = {
        ...state.jobData,
        ...action.payload,
      };
      setTimeout(() => {
        callback && callback();
      }, 50);
    },
    /**
     * Updates the loading state for the getDayWiseApiCall.
     *
     * @param {type} state - the current state
     * @param {type} action - the action to be performed
     * @return {type} the updated state with getDayWiseApiLoading
     */
    updateGetDayWiseApiCallLoading: (state, action) => {
      if (action.payload == "-") {
        state.getDayWiseApiLoading = --state.getDayWiseApiLoading;
      } else {
        state.getDayWiseApiLoading = ++state.getDayWiseApiLoading;
      }
    },
    /**
     * Function to update the setAsTemplate property in the jobData object of the state.
     *
     * @param {type} state - The current state object
     * @param {type} action - The action object containing the payload
     * @return {type}
     */
    updateSetAsTemplate: (state, action) => {
      state.jobData = {
        ...state.jobData,
        setAsTemplate: action.payload,
      };
    },
    /**
     * Resets the page state with the given action.
     *
     * @param {Object} state - the current state
     * @param {Object} action - the action object
     * @return {Object} the updated state after resetting the page
     */
    resetPage: (state, action) => {
      // let contractTemplates = state.contractTemplates;
      // let candidateRoleList = state.candidateRoleList;
      let jobData = state.jobData;
      state.activeStep = 1;
      state.isLoading = false;
      state.skillsList = [];
      state.activeTemplate = null;
      state.stepsValidated = initialState.stepsValidated;
      ((state.jobData = {
        ...initialState.jobData,
        admin_per: jobData.admin_per,
        visit_per: jobData.visit_per,
        // noOfVisit: jobData.noOfVisit,
      }),
        (state.jobShiftObj = []));
      state.selectedJobShiftObj = [];
      state.jobShiftObjFilters = initialState.jobShiftObjFilters;
      state.getDayWiseApiLoading = initialState.getDayWiseApiLoading;
      state.enableMultipleShiftEditing =
        initialState.enableMultipleShiftEditing;
      state.firstMultipleSelectedJobShiftObj =
        initialState.firstMultipleSelectedJobShiftObj;
      state.toggleSelected = initialState.toggleSelected;
      state.isUnitsValid = initialState.isUnitsValid;
      state.isStartEndDateDisabled = initialState.isStartEndDateDisabled;
      state.repeatObj = defaultRepeatObj;
      state.customRecurrenceModalOpen = false;
    },
    /**
     * Updates the job shift object filters in the state with the provided action payload.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing the payload
     * @return {void}
     */
    updateJobShiftObjFilters: (state, action) => {
      state.jobShiftObjFilters = {
        ...state.jobShiftObjFilters,
        ...action.payload,
      };
    },
    /**
     * updateSelectedJobShiftObj - Updates the selected job shift object in the state.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing the payload
     * @return {void}
     */
    updateSelectedJobShiftObj: (state, action) => {
      state.selectedJobShiftObj = action.payload;
    },
    /**
     * Toggles multiple shift editing.
     *
     * @param {type} state - description of state parameter
     * @param {type} action - description of action parameter
     * @return {type} description of return value
     */
    toggleMultipleShiftEditing: (state, action) => {
      state.enableMultipleShiftEditing = action.payload;
    },
    /**
     * Update the first multiple selected job shift object in the state.
     *
     * @param {type} state - the current state
     * @param {type} action - the action containing the payload
     * @return {type} undefined
     */
    updateFirstMultipleSelectedJobShiftObj: (state, action) => {
      state.firstMultipleSelectedJobShiftObj = action.payload;
    },
    /**
     * Update the repeatObj property of the state with the values from the action payload.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing payload
     */
    updateRepeatObj: (state, action) => {
      state.repeatObj = {
        ...state.repeatObj,
        ...action.payload,
      };
    },
    /**
     * Function to toggle the custom recurrence modal open state.
     *
     * @param {type} state - the current state
     * @param {type} action - the action object
     * @return {type} undefined
     */
    toggleCustomRecurrenceModalOpen: (state, action) => {
      state.customRecurrenceModalOpen = action.payload;
    },
    resetDateAndTime: (state, action) => {
      state.jobData = {
        ...state.jobData,
        // rateCardList: ,
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        noOfVisit: 0,
        breakTime: "00:00",
        adminTime: "00:00",
        totalDays: 0,
        setAsTemplate: false,
        totalHours: 0,
        totalCost: 0,
        totalCandidatePay: 0,
        admin_per: 0,
        applocumServiceFee: 0,
      };
      state.jobShiftObj = [];
      state.stepsValidated = initialState.stepsValidated;
      state.selectedJobShiftObj = [];
      state.jobShiftObjFilters = initialState.jobShiftObjFilters;
      state.firstMultipleSelectedJobShiftObj = {};
      state.repeatObj = defaultRepeatObj;
      state.isStartEndDateDisabled = false;
    },
    updateRecurrenceList: (state, action) => {
      let dates = [];
      const {
        startsOn: STARTS_ON,
        endsOn: ENDS_ON,
        frequency: FREQUENCY,
        everyFrequency: EVERY_FREQUENCY,
        recurrenceWeekDays: RECURRENCE_WEEK_DAYS,
        recurrenceMonthDates: RECURRENCE_MONTH_DATES,
        recurrenceYearDayOfWeek: RECURRENCE_YEAR_DAY_OF_WEEK,
        recurrenceYearWeekDay: RECURRENCE_YEAR_WEEK_DAY,
        recurrenceYearMonths: RECURRENCE_YEAR_MONTHS,
      } = state.repeatObj;
      const {
        weekDaysNamesPresets: WEEK_DAYS_NAMES,
        daysOfWeekPresets: DAYS_OF_WEEK,
      } = state.recurringListingPresets;
      if (STARTS_ON === null || ENDS_ON === null) {
        return;
      }
      let _startDate = moment(STARTS_ON, "DD-MM-YYYY");
      let _endDate = moment(ENDS_ON, "DD-MM-YYYY");
      let _startDateSTR = STARTS_ON;
      let _endDateSTR = ENDS_ON;
      let text = "";
      if (FREQUENCY === "daily") {
        text = `Shift will occur every ${EVERY_FREQUENCY == 1 ? "day" : EVERY_FREQUENCY + " days"}`;

        while (_startDate <= _endDate) {
          dates.push(_startDate.format("DD-MM-YYYY"));
          _startDate = _startDate.add(EVERY_FREQUENCY, "days");
        }
      } else if (FREQUENCY === "weekly") {
        let daysName = "";

        let sortedDays = RECURRENCE_WEEK_DAYS.sort(
          (a, b) => WEEK_DAYS_NAMES.indexOf(a) - WEEK_DAYS_NAMES.indexOf(b),
        ); // Sorting selected week days

        let RECURRENCE_WEEK_DAYS_LENGTH = RECURRENCE_WEEK_DAYS.length;

        sortedDays.forEach((day, index) => {
          if (RECURRENCE_WEEK_DAYS_LENGTH === 1) {
            daysName += `${day}`;
          } else {
            if (
              RECURRENCE_WEEK_DAYS_LENGTH > 1 &&
              index == RECURRENCE_WEEK_DAYS_LENGTH - 1
            ) {
              daysName += ` and ${day}`;
            } else if (index == RECURRENCE_WEEK_DAYS_LENGTH - 2) {
              daysName += `${day}`;
            } else {
              daysName += `${day}, `;
            }
          }
        }); // week days text

        text = `Shift will occur every ${EVERY_FREQUENCY == 1 ? "week" : EVERY_FREQUENCY + " weeks"}`;
        if (daysName) {
          text += ` on ${daysName}.`;
        }

        if (EVERY_FREQUENCY === 1) {
          // if every frequency is 1
          let _weekDays = [];
          while (_startDate <= _endDate) {
            let isDayInSelection = RECURRENCE_WEEK_DAYS.includes(
              _startDate.format("dddd"),
            );
            if (isDayInSelection) {
              let date = _startDate.format("DD-MM-YYYY");
              if (isDateBetween(date, _startDateSTR, _endDateSTR)) {
                _weekDays.push(date);
              }
            }
            if (_startDate === _endDate) {
              break;
            }
            _startDate = _startDate.add(1, "days");
            if (
              _startDate.month() === _endDate.month() &&
              _startDate.year() === _endDate.year() &&
              _startDate.isAfter(_endDate)
            ) {
              _startDate = _endDate;
            }
          }
          dates.push(..._weekDays);
        } else {
          while (_startDate <= _endDate) {
            let _curWeek = _startDate.week();
            let _curYear = _startDate.year();
            let _weekDays = [];
            RECURRENCE_WEEK_DAYS.forEach((day) => {
              let date = moment()
                .day(day)
                .week(_curWeek)
                .year(_curYear)
                .format("DD-MM-YYYY");
              if (isDateBetween(date, _startDateSTR, _endDateSTR)) {
                _weekDays.push(date);
              }
            });
            dates.push(..._weekDays);
            _startDate = _startDate.add(EVERY_FREQUENCY, "weeks");
          }
        }
      } else if (FREQUENCY === "monthly") {
        let monthDates = "";

        let sortedNums = RECURRENCE_MONTH_DATES.sort((a, b) => a - b); // sorting selected month dates

        let RECURRENCE_MONTH_DATES_LENGTH = RECURRENCE_MONTH_DATES.length;

        sortedNums.forEach((num) => {
          let _date = moment(`${num}-01`, "DD-MM").format("Do");
          if (RECURRENCE_MONTH_DATES_LENGTH === 1) {
            monthDates += ` ${_date}.`;
          } else {
            if (
              RECURRENCE_MONTH_DATES_LENGTH > 1 &&
              num == RECURRENCE_MONTH_DATES[RECURRENCE_MONTH_DATES_LENGTH - 1]
            ) {
              monthDates += ` and ${_date}.`;
            } else if (
              num == RECURRENCE_MONTH_DATES[RECURRENCE_MONTH_DATES_LENGTH - 2]
            ) {
              monthDates += ` ${_date}`;
            } else {
              monthDates += ` ${_date},`;
            }
          }
        }); // Month dates text

        text = `Shift will occur every ${EVERY_FREQUENCY == 1 ? "month" : EVERY_FREQUENCY + " months"}`;
        if (monthDates) {
          text += ` on ${monthDates}`;
        }

        if (EVERY_FREQUENCY === 1) {
          // if every frequency is 1
          let _monthDates = [];
          while (_startDate <= _endDate) {
            let isDateInSelection = RECURRENCE_MONTH_DATES.includes(
              _startDate.date(),
            );
            if (isDateInSelection) {
              let date = _startDate.format("DD-MM-YYYY");
              if (isDateBetween(date, _startDateSTR, _endDateSTR)) {
                _monthDates.push(date);
              }
            }
            if (_startDate === _endDate) {
              break;
            }
            _startDate = _startDate.add(1, "days");
            if (
              _startDate.month() === _endDate.month() &&
              _startDate.year() === _endDate.year() &&
              _startDate.isAfter(_endDate)
            ) {
              _startDate = _endDate;
            }
          }
          dates.push(..._monthDates);
        } else {
          while (_startDate <= _endDate) {
            let _curMonth = _startDate.month();
            let _curYear = _startDate.year();
            let _monthDates = [];
            RECURRENCE_MONTH_DATES.forEach((num) => {
              let date = moment()
                .date(num)
                .month(_curMonth)
                .year(_curYear)
                .format("DD-MM-YYYY");
              if (isDateBetween(date, _startDateSTR, _endDateSTR)) {
                if (moment(date, "DD-MM-YYYY").get("date") == num) {
                  _monthDates.push(date);
                }
              }
            });
            dates.push(..._monthDates);
            _startDate = _startDate.add(EVERY_FREQUENCY, "months");
          }
        }
      } else if (FREQUENCY === "yearly") {
        let yearDayOfWeekIndex = []; // 0, 1, 2, 3, 4, 5, 6
        DAYS_OF_WEEK.forEach((day, index) => {
          if (RECURRENCE_YEAR_DAY_OF_WEEK.includes(day)) {
            yearDayOfWeekIndex.push(index);
          }
        }); // making dayofweek index

        let yearWeekDayIndex = [];
        WEEK_DAYS_NAMES.forEach((day, index) => {
          if (RECURRENCE_YEAR_WEEK_DAY.includes(day)) {
            yearWeekDayIndex.push(index);
          }
        }); // making weekdaynames index

        let sortedDays = RECURRENCE_YEAR_DAY_OF_WEEK.sort(
          (a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b),
        ); // Sorting Selected Days Of Week

        let sortedWeekDays = RECURRENCE_YEAR_WEEK_DAY.sort(
          (a, b) => WEEK_DAYS_NAMES.indexOf(a) - WEEK_DAYS_NAMES.indexOf(b),
        ); // Sorting Selected Week Days

        let sortedMonths = RECURRENCE_YEAR_MONTHS.sort(
          (a, b) => MONTH_NAMES.indexOf(a) - WEEK_DAYS_NAMES.indexOf(b),
        ); // Sorting Selected Months

        let dayOfWeekSTR = "";
        let weekDaySTR = "";
        let yearMonthsSTR = "";

        let RECURRENCE_YEAR_DAY_OF_WEEK_LENGTH =
          RECURRENCE_YEAR_DAY_OF_WEEK.length;
        let RECURRENCE_YEAR_WEEK_DAY_LENGTH = RECURRENCE_YEAR_WEEK_DAY.length;
        let RECURRENCE_YEAR_MONTHS_LENGTH = RECURRENCE_YEAR_MONTHS.length;

        sortedDays.forEach((day, index) => {
          if (RECURRENCE_YEAR_DAY_OF_WEEK_LENGTH === 1) {
            dayOfWeekSTR += `${day}`;
          } else {
            if (
              RECURRENCE_YEAR_DAY_OF_WEEK_LENGTH > 1 &&
              index == RECURRENCE_YEAR_DAY_OF_WEEK_LENGTH - 1
            ) {
              dayOfWeekSTR += ` and ${day}`;
            } else if (index == RECURRENCE_YEAR_DAY_OF_WEEK_LENGTH - 2) {
              dayOfWeekSTR += `${day}`;
            } else {
              dayOfWeekSTR += `${day}, `;
            }
          }
        }); // Days Of Week Text

        sortedWeekDays.forEach((day, index) => {
          if (RECURRENCE_YEAR_WEEK_DAY_LENGTH === 1) {
            weekDaySTR += `${day}`;
          } else {
            if (
              RECURRENCE_YEAR_WEEK_DAY_LENGTH > 1 &&
              index == RECURRENCE_YEAR_WEEK_DAY_LENGTH - 1
            ) {
              weekDaySTR += ` and ${day}`;
            } else if (index == RECURRENCE_YEAR_WEEK_DAY_LENGTH - 2) {
              weekDaySTR += `${day}`;
            } else {
              weekDaySTR += `${day}, `;
            }
          }
        }); // Week Days Text

        sortedMonths.forEach((month, index) => {
          if (RECURRENCE_YEAR_MONTHS_LENGTH === 1) {
            yearMonthsSTR += `${month}`;
          } else {
            if (
              RECURRENCE_YEAR_MONTHS_LENGTH > 1 &&
              index == RECURRENCE_YEAR_MONTHS_LENGTH - 1
            ) {
              yearMonthsSTR += ` and ${month}`;
            } else if (index == RECURRENCE_YEAR_MONTHS_LENGTH - 2) {
              yearMonthsSTR += `${month}`;
            } else {
              yearMonthsSTR += `${month}, `;
            }
          }
        }); // Months Text

        text = `Shift will occur every ${EVERY_FREQUENCY == 1 ? "year" : EVERY_FREQUENCY + " years"}`;
        if (yearMonthsSTR && dayOfWeekSTR && weekDaySTR) {
          text += ` on ${dayOfWeekSTR} ${weekDaySTR} of ${yearMonthsSTR}.`;
        }

        if (EVERY_FREQUENCY === 1) {
          // if every frequency is 1
          let _yearMonthsDates = [];
          while (_startDate <= _endDate) {
            let isMonthInSelection = RECURRENCE_YEAR_MONTHS.includes(
              _startDate.format("MMMM"),
            );
            if (isMonthInSelection) {
              yearDayOfWeekIndex.forEach((dayOfWeekIndex) => {
                let dayOfWeekName = DAYS_OF_WEEK[dayOfWeekIndex];
                yearWeekDayIndex.forEach((weekDayIndex) => {
                  let weekDayName = WEEK_DAYS_NAMES[weekDayIndex];
                  let date = getGivenDateOfMonth(
                    moment().month(_startDate.month()).year(_startDate.year()),
                    weekDayIndex,
                    dayOfWeekIndex,
                  ).format("DD-MM-YYYY");
                  let isDateInMonth =
                    moment(date, "DD-MM-YYYY").month() === _startDate.month();
                  if (isDateInMonth) {
                    if (isDateBetween(date, _startDateSTR, _endDateSTR)) {
                      _yearMonthsDates.push(date);
                    }
                  } else {
                    let isDateIsInNextMonth =
                      moment(date, "DD-MM-YYYY").month() ===
                      _startDate.month() + 1;
                    if (isDateIsInNextMonth && dayOfWeekName === "Last") {
                      let lastWeekDay = moment()
                        .month(_startDate.month())
                        .year(_startDate.year())
                        .endOf("month")
                        .day(weekDayName);
                      date = lastWeekDay.format("DD-MM-YYYY");
                      if (isDateBetween(date, _startDateSTR, _endDateSTR)) {
                        _yearMonthsDates.push(date);
                      }
                    }
                  }
                });
              });
            }
            if (_startDate === _endDate) {
              break;
            }
            _startDate = _startDate.add(1, "months");
            if (
              _startDate.month() === _endDate.month() &&
              _startDate.year() === _endDate.year() &&
              _startDate.isAfter(_endDate)
            ) {
              _startDate = _endDate;
            }
          }
          dates.push(..._yearMonthsDates);
        } else {
          while (_startDate <= _endDate) {
            let _curYear = _startDate.year();
            let _yearMonthsDates = [];
            RECURRENCE_YEAR_MONTHS.forEach((month) => {
              let monthIndex = MONTH_NAMES.indexOf(month);
              yearDayOfWeekIndex.forEach((dayOfWeekIndex) => {
                let dayOfWeekName = DAYS_OF_WEEK[dayOfWeekIndex];
                yearWeekDayIndex.forEach((weekDayIndex) => {
                  let weekDayName = WEEK_DAYS_NAMES[weekDayIndex];
                  let date = getGivenDateOfMonth(
                    moment().month(monthIndex).year(_curYear),
                    weekDayIndex,
                    dayOfWeekIndex,
                  ).format("DD-MM-YYYY");
                  let isDateInMonth =
                    moment(date, "DD-MM-YYYY").month() === monthIndex;
                  if (isDateInMonth) {
                    if (isDateBetween(date, _startDateSTR, _endDateSTR)) {
                      _yearMonthsDates.push(date);
                    }
                  } else {
                    let isDateIsInNextMonth =
                      moment(date, "DD-MM-YYYY").month() === monthIndex + 1;
                    if (isDateIsInNextMonth && dayOfWeekName === "Last") {
                      let lastWeekDay = moment()
                        .month(monthIndex)
                        .year(_curYear)
                        .endOf("month")
                        .day(weekDayName);
                      date = lastWeekDay.format("DD-MM-YYYY");
                      if (isDateBetween(date, _startDateSTR, _endDateSTR)) {
                        _yearMonthsDates.push(date);
                      }
                    }
                  }
                });
              });
            });
            dates.push(..._yearMonthsDates);
            if (_startDate === _endDate) {
              break;
            }
            _startDate = _startDate.add(EVERY_FREQUENCY, "years");
            if (
              _startDate.year() === _endDate.year() &&
              _startDate.isAfter(_endDate)
            ) {
              _startDate = _endDate;
            }
          }
        }
      }
      // console.log(_.uniq(dates));
      state.repeatObj = {
        ...state.repeatObj,
        shiftOccurText: text,
        dates: _.uniq(dates),
      };
    },
    updateIsDisabledStartEndDate: (state, action) => {
      state.isStartEndDateDisabled = action.payload;
    },
    resetRepeatObj: (state, action) => {
      state.repeatObj = {
        ...defaultRepeatObj,
        repeat: state.repeatObj.repeat,
        frequency: state.repeatObj.frequency,
        startsOn: state.repeatObj.startsOn,
        endsOn: state.repeatObj.endsOn,
        dates: [],
        shiftOccurText: "",
      };
    },
    updateFromDraft: (state, action) => {
      // console.log(action.payload,state
      // state = {
      //   ...state,
      //   ...action.payload,
      // }
      return {
        ...state,
        ...action.payload,
      };
    },

    toggleInvitedDirectBankCandidateForContractModal: (state, action) => {
      state.invitedBankCandidateForContractModal = action.payload;
    },
    updatePagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    updateBankShortListCandidatesList: (state, action) => {
      state.shortlistCandidateData = action.payload;
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setBankShortListCandidatesLoading: (state, action) => {
      state.isShortListLoading = action.payload;
    },
    updateSelectedCandidate: (state, action) => {
      state.selectedShortlistCandidate = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    updateSelectedShortListCandidateIds: (state, action) => {
      state.selectedCandidateIds = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
    deSelectShortListAllCandidate: (state, action) => {
      state.selectedShortlistCandidate = [];
      state.isSelectedAll = false;
      state.selectedCandidateIds = [];
    },

    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
  },
});

export const {
  updateActiveStep,
  updateLoading,
  updateTemplateLoading,
  updateStepsValidated,
  updateContractTemplatesList,
  updateActiveTemplate,
  updateActiveTemplateType,
  updateCandidateRoleList,
  updateJobData,
  updateSkillsList,
  updateJobShiftList,
  updateSingleJobShift,
  updateToggleSelected,
  updateJobShiftObjFromLocal,
  removeDayFromJobShiftObj,
  updateGetDayWiseApiCallLoading,
  updateSetAsTemplate,
  resetPage,
  updateJobShiftObjFilters,
  updateSelectedJobShiftObj,
  toggleMultipleShiftEditing,
  updateFirstMultipleSelectedJobShiftObj,
  updateRepeatObj,
  toggleCustomRecurrenceModalOpen,
  resetDateAndTime,
  updateRecurrenceList,
  updateIsDisabledStartEndDate,
  resetRepeatObj,
  updateFromDraft,
  toggleInvitedDirectBankCandidateModal,
  toggleInvitedDirectBankCandidateForContractModal,
  updatePagination,
  updateTotalCount,
  updateBankShortListCandidatesList,
  setBankShortListCandidatesLoading,
  updateSelectedShortListCandidateIds,
  toggleSelectedAll,
  deSelectShortListAllCandidate,
  updateFilters,
} = clientCreateJobSlice.actions;

export default clientCreateJobSlice.reducer;
